/**
 * LiveDemoPage — public read-only Norway demo dashboard.
 *
 * URL: /live?country=NO&share=<token>
 *
 * NO admin shell, NO nav, NO private data. Polls
 * `GET /api/demo/live?country=&share=` every 10 s.
 * Shows LIVE badge, KPIs (5m / 24h / top country), 5-minute heartbeat
 * sparkline, and the last 20 anonymized events (ts · country · device).
 *
 * If the server returns `{ok:false}`, we render a clean "Link expired"
 * card with an instruction to request a fresh QR from the host.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 10000;

const fmtClock = (d) => {
  if (!d) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  const x = d instanceof Date ? d : new Date(d);
  if (isNaN(x.getTime())) return '—';
  return `${pad(x.getHours())}:${pad(x.getMinutes())}:${pad(x.getSeconds())}`;
};

// Minimal public-page i18n. Public /live has no language picker —
// pick from `?lang=` URL param first, then navigator.language.
// Keys are only for the new trend / ready-state strings. Existing
// KPI titles remain English (the admin modal's Share URL can append
// `?lang=xx` when routing a specific partner).
const TR = {
  en: {
    trendLegend: 'vs previous 5 min',
    new: 'NEW',
    readyTitle: 'System is ready',
    readyBody: 'Scan the demo QR to generate real activity — the heatmap and arrows will fill up live.',
    trendDash: '—',
    trend24Prefix: '24h',
    liveActivityLast5m: 'Live activity · last 5 min',
    moreDarker: 'EE / FI / NO · more activity = darker',
    privacyNote: 'Privacy-safe: aggregated by country only · no personal data.',
    total5mLabel: 'Total (5m)',
    legendLess: 'Less',
    legendMore: 'More',
    peakTitle: 'Peak activity!',
    peakSub: 'All-time 5-min record',
  },
  et: {
    trendLegend: 'eelneva 5 min võrdlus',
    new: 'UUS',
    readyTitle: 'Süsteem on valmis',
    readyBody: 'Skaneeri demo QR — soojuskaart ja nooled täituvad live\'is.',
    trendDash: '—',
    trend24Prefix: '24h',
    liveActivityLast5m: 'Live tegevus · viimased 5 min',
    moreDarker: 'EE / FI / NO · rohkem tegevust = tumedam',
    privacyNote: 'Privaatsus: agregeeritud riigi tasandil · isikuandmeid ei näidata.',
    total5mLabel: 'Kokku (5m)',
    legendLess: 'Vähem',
    legendMore: 'Rohkem',
    peakTitle: 'Tipuaktiivsus!',
    peakSub: 'Uus 5-min rekord',
  },
  fi: {
    trendLegend: 'vs. edelliset 5 min',
    new: 'UUSI',
    readyTitle: 'Järjestelmä on valmis',
    readyBody: 'Skannaa demo-QR — lämpökartta ja nuolet päivittyvät reaaliajassa.',
    trendDash: '—',
    trend24Prefix: '24h',
    liveActivityLast5m: 'Live-toiminta · viimeiset 5 min',
    moreDarker: 'EE / FI / NO · enemmän toimintaa = tummempi',
    privacyNote: 'Yksityisyys: vain maatason aggregointi · ei henkilötietoja.',
    total5mLabel: 'Yht. (5m)',
    legendLess: 'Vähemmän',
    legendMore: 'Enemmän',
    peakTitle: 'Huippuaktiviteetti!',
    peakSub: 'Uusi 5-min ennätys',
  },
  nb: {
    trendLegend: 'vs. forrige 5 min',
    new: 'NY',
    readyTitle: 'Systemet er klart',
    readyBody: 'Skann demo-QR-en — varmekartet og pilene fylles live.',
    trendDash: '—',
    trend24Prefix: '24t',
    liveActivityLast5m: 'Live-aktivitet · siste 5 min',
    moreDarker: 'EE / FI / NO · mer aktivitet = mørkere',
    privacyNote: 'Personvern: aggregert på landsnivå · ingen personopplysninger.',
    total5mLabel: 'Totalt (5m)',
    legendLess: 'Mindre',
    legendMore: 'Mer',
    peakTitle: 'Topp aktivitet!',
    peakSub: 'Ny 5-min rekord',
  },
  ru: {
    trendLegend: 'по сравнению с предыдущими 5 мин',
    new: 'NEW',
    readyTitle: 'Система готова',
    readyBody: 'Отсканируйте демо-QR — тепловая карта и стрелки заполнятся в реальном времени.',
    trendDash: '—',
    trend24Prefix: '24ч',
    liveActivityLast5m: 'Активность · последние 5 мин',
    moreDarker: 'EE / FI / NO · больше активности = темнее',
    privacyNote: 'Приватность: агрегация только по странам · без персональных данных.',
    total5mLabel: 'Всего (5м)',
    legendLess: 'Меньше',
    legendMore: 'Больше',
    peakTitle: 'Пик активности!',
    peakSub: 'Новый рекорд за 5 мин',
  },
};
const pickLang = () => {
  try {
    const u = new URL(window.location.href);
    const q = (u.searchParams.get('lang') || '').toLowerCase();
    if (TR[q]) return q;
    const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
    // Map common Norwegian codes to nb.
    if (nav === 'no') return 'nb';
    return TR[nav] ? nav : 'en';
  } catch (e) { return 'en'; }
};

// ----- Nordic heatmap (EE / FI / NO) -----
// Stylized inline-SVG country shapes — honest, minimal, no external map
// libs. Country-level aggregation only; no PII used.
// Shapes are simplified silhouettes placed geographically (NO west,
// FI east, EE south of FI across the Gulf of Finland).
const COUNTRY_SHAPES = {
  NO: 'M88,28 L102,14 L116,22 L122,40 L118,70 L134,82 L128,104 L140,130 L128,160 L112,188 L98,214 L84,236 L70,228 L60,204 L54,176 L46,150 L52,130 L48,100 L58,76 L66,54 L74,40 Z',
  FI: 'M158,36 L176,22 L200,36 L214,70 L212,102 L220,140 L208,178 L186,210 L168,218 L154,202 L146,172 L140,140 L136,108 L142,74 L150,52 Z',
  EE: 'M162,226 L188,218 L214,222 L226,238 L222,258 L204,266 L180,264 L162,252 Z',
};

const intensityColor = (ratio) => {
  // 4 discrete steps so the UI stays stable & readable.
  if (ratio <= 0)   return { fill: '#e2e8f0', stroke: '#cbd5e1' };     // slate-200
  if (ratio < 0.34) return { fill: '#a7f3d0', stroke: '#6ee7b7' };     // emerald-200
  if (ratio < 0.67) return { fill: '#34d399', stroke: '#10b981' };     // emerald-400
  if (ratio < 1.0)  return { fill: '#10b981', stroke: '#059669' };     // emerald-500
  return { fill: '#047857', stroke: '#065f46' };                        // emerald-700 (max)
};

const TrendBadge = ({ trend, l, dimmed = false }) => {
  if (!trend) {
    return <span className="inline-block text-xs font-mono text-slate-400 w-14 text-right" data-testid="trend-dash">{l.trendDash}</span>;
  }
  const hint = trend.hint5 || 'flat';
  if (dimmed || hint === 'zero') {
    return <span className="inline-block text-xs font-mono text-slate-400 w-14 text-right" data-testid="trend-dash">{l.trendDash}</span>;
  }
  const color = hint === 'up' || hint === 'new' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : hint === 'down' ? 'text-rose-700 bg-rose-50 border-rose-200'
    : 'text-slate-500 bg-slate-50 border-slate-200';
  const label = hint === 'new' ? l.new
    : trend.pct5 === null ? l.new
    : `${trend.pct5 > 0 ? '+' : ''}${trend.pct5}%`;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-bold border px-1.5 py-0.5 rounded ${color} w-14 justify-end tabular-nums`}
      data-testid="trend-badge"
      title={`${trend.last5} vs ${trend.prev5} ${l.trendLegend}`}
    >
      <span>{trend.arrow5}</span>
      <span>{label}</span>
    </span>
  );
};

const NordicHeatmap = ({ counts = {}, totalLast5m = 0, online = true, trends = null, readyMode = false, l }) => {
  const values = ['EE', 'FI', 'NO'].map((k) => counts?.[k] || 0);
  const max = Math.max(1, ...values);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="live-heatmap">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">{l.liveActivityLast5m}</div>
            {online && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700" data-testid="heatmap-live-dot">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                </span>
                LIVE
              </span>
            )}
          </div>
          <div className="text-sm text-slate-600">{l.moreDarker}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{l.total5mLabel}</div>
          <div className="text-2xl font-black text-emerald-700 tabular-nums" data-testid="heatmap-total">{totalLast5m || 0}</div>
        </div>
      </div>

      {readyMode && (
        <div
          className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm flex items-start gap-2"
          data-testid="heatmap-ready-banner"
        >
          <span className="text-lg leading-none">🟢</span>
          <div>
            <div className="font-bold">{l.readyTitle}</div>
            <div className="text-xs text-amber-800/90">{l.readyBody}</div>
          </div>
        </div>
      )}

      <div className="flex items-start gap-6 flex-wrap">
        <svg
          viewBox="0 0 280 290"
          className="w-56 h-auto"
          role="img"
          aria-label="Nordic heatmap — Norway, Finland, Estonia"
        >
          {/* Subtle sea background for geographic context */}
          <rect x="0" y="0" width="280" height="290" fill="#f8fafc" rx="12" />
          {['NO', 'FI', 'EE'].map((code) => {
            const c = counts?.[code] || 0;
            const ratio = readyMode ? 0 : c / max;
            const { fill, stroke } = intensityColor(ratio);
            return (
              <g key={code}>
                <path
                  d={COUNTRY_SHAPES[code]}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1.5"
                  data-testid={`heatmap-shape-${code}`}
                >
                  <title>{`${code}: ${c} open${c === 1 ? '' : 's'} (last 5m)`}</title>
                </path>
              </g>
            );
          })}
          {/* Country labels — overlay centroids */}
          <g fontFamily="system-ui, sans-serif" fontWeight="700" textAnchor="middle">
            <text x="90" y="132" fontSize="12" fill="#0f172a">NO</text>
            <text x="178" y="120" fontSize="12" fill="#0f172a">FI</text>
            <text x="194" y="250" fontSize="11" fill="#0f172a">EE</text>
          </g>
        </svg>

        <div className="flex-1 min-w-[200px] space-y-2">
          {['NO', 'FI', 'EE'].map((code) => {
            const c = counts?.[code] || 0;
            const ratio = readyMode ? 0 : c / max;
            const { fill } = intensityColor(ratio);
            const t = trends?.[code] || null;
            return (
              <div key={code} className="flex items-center gap-3" data-testid={`heatmap-row-${code}`}>
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: fill }} />
                <span className="text-sm font-semibold text-slate-700 w-6">{code}</span>
                <span className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <span
                    className="block h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${Math.max(3, Math.round(ratio * 100))}%` }}
                  />
                </span>
                <span className="text-sm font-mono tabular-nums text-slate-900 w-8 text-right">{c}</span>
                <TrendBadge trend={t} l={l} dimmed={readyMode} data-testid={`trend-${code}`} />
              </div>
            );
          })}

          <div className="pt-2 flex items-center gap-1.5 text-[10px] text-slate-400">
            <span>{l.legendLess}</span>
            <span className="inline-block w-4 h-2 rounded bg-slate-200" />
            <span className="inline-block w-4 h-2 rounded bg-emerald-200" />
            <span className="inline-block w-4 h-2 rounded bg-emerald-400" />
            <span className="inline-block w-4 h-2 rounded bg-emerald-500" />
            <span className="inline-block w-4 h-2 rounded bg-emerald-700" />
            <span>{l.legendMore}</span>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-3">{l.privacyNote}</p>
    </div>
  );
};

const Sparkline = ({ data = [], width = 320, height = 64 }) => {
  if (!data.length) return <svg width={width} height={height} />;
  const max = Math.max(1, ...data.map((d) => d.count || 0));
  const step = width / Math.max(1, data.length - 1);
  const padTop = 4;
  const padBot = 4;
  const h = height - padTop - padBot;
  const points = data.map((d, i) => [i * step, padTop + h - ((d.count || 0) / max) * h]);
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${points[points.length - 1][0].toFixed(1)},${(height - padBot).toFixed(1)} L0,${(height - padBot).toFixed(1)} Z`;
  return (
    <svg width={width} height={height} className="w-full" data-testid="live-sparkline" role="img" aria-label="demo opens last 5 minutes">
      <path d={areaD} fill="rgba(16,185,129,0.15)" />
      <path d={pathD} fill="none" stroke="#10b981" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {points.map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={data[i].count > 0 ? 3 : 1.5} fill={data[i].count > 0 ? '#10b981' : '#cbd5e1'} />
          <rect x={x - step / 2} y={0} width={step} height={height} fill="transparent">
            <title>{`${fmtClock(data[i].t_start)}–${fmtClock(data[i].t_end)}: ${data[i].count}`}</title>
          </rect>
        </g>
      ))}
    </svg>
  );
};

const Kpi = ({ label, value, tone = 'emerald' }) => {
  const bg = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  }[tone];
  return (
    <div className={`rounded-xl border ${bg} px-5 py-4`}>
      <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-3xl font-black mt-1 tabular-nums">{value}</div>
    </div>
  );
};

// ----- Peak Activity sticker (8-second confetti-free overlay) -----
const PeakSticker = ({ peakAt, peakValue, l }) => {
  const [visible, setVisible] = useState(false);
  // Dedupe across polls within a tab: remember the `last_peak_at` we
  // already showed, so if the server repeats `is_new_peak:true` (network
  // retry edge case) we don't re-trigger.
  useEffect(() => {
    if (!peakAt) return;
    let shown = null;
    try { shown = sessionStorage.getItem('keikka_peak_shown_at'); } catch (_) {}
    if (shown === peakAt) return;
    try { sessionStorage.setItem('keikka_peak_shown_at', peakAt); } catch (_) {}
    setVisible(true);
    const id = setTimeout(() => setVisible(false), 8000);
    return () => clearTimeout(id);
  }, [peakAt]);

  if (!visible) return null;
  return (
    <div
      className="fixed top-4 right-4 z-50 bg-gradient-to-br from-amber-400 to-amber-600 text-white rounded-2xl shadow-xl border border-amber-300 px-5 py-3 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300"
      data-testid="peak-sticker"
      role="status"
      aria-live="polite"
    >
      <span className="text-3xl leading-none" aria-hidden="true">🏆</span>
      <div>
        <div className="font-black text-base leading-tight" data-testid="peak-title">{l.peakTitle}</div>
        <div className="text-[11px] font-semibold opacity-90">
          {l.peakSub} · <span className="font-mono tabular-nums">{peakValue}</span>
        </div>
      </div>
    </div>
  );
};

const LiveDemoPage = () => {
  const [params] = useSearchParams();
  const country = (params.get('country') || '').toUpperCase();
  const share = params.get('share') || '';
  const lang = useMemo(() => pickLang(), []);
  const l = TR[lang] || TR.en;

  const [state, setState] = useState({ loading: true, ok: null, error: null, data: null });
  const [lastOkAt, setLastOkAt] = useState(null);
  const [online, setOnline] = useState(true);

  const load = useCallback(async () => {
    if (!country || !share) {
      setState({ loading: false, ok: false, error: 'missing_params', data: null });
      return;
    }
    try {
      const res = await axios.get(`${API}/api/demo/live`, { params: { country, share } });
      if (res.data?.ok) {
        setState({ loading: false, ok: true, error: null, data: res.data });
        setLastOkAt(new Date());
        setOnline(true);
      } else {
        setState({ loading: false, ok: false, error: res.data?.error || 'invalid', data: null });
      }
    } catch (e) {
      setOnline(false);
    }
  }, [country, share]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const data = state.data;
  const recent = data?.recent || [];
  const series = data?.series || [];

  const expiresLabel = useMemo(() => {
    if (!data?.expires_at) return null;
    try {
      const d = new Date(data.expires_at);
      return d.toLocaleString();
    } catch (e) { return null; }
  }, [data]);

  // --- Error state ---
  if (!state.loading && state.ok === false) {
    const msg = {
      expired: 'This live demo link has expired.',
      revoked: 'This live demo link has been revoked.',
      not_found: 'This live demo link is invalid.',
      country_mismatch: 'This link is for a different country.',
      missing_params: 'Missing country or share parameter.',
      invalid: 'This live demo link is invalid.',
    }[state.error] || 'This live demo link is not available.';
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="live-expired">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="text-5xl mb-3">⌛</div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">{msg}</h1>
          <p className="text-sm text-slate-600">Ask the host for a new QR code.</p>
        </div>
      </div>
    );
  }

  // --- Loading state ---
  if (state.loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-slate-500 text-sm">Loading live demo…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="live-demo-page">
      {/* Peak Activity overlay — floats top-right, auto-hides after 8s */}
      {data?.peak?.is_new_peak && (
        <PeakSticker
          peakAt={data.peak.last_peak_at || data.peak.now}
          peakValue={data.peak.peak_5m_all_time}
          l={l}
        />
      )}

      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white font-black flex items-center justify-center shadow-sm">K</div>
            <div>
              <div className="text-base font-bold text-slate-900">Keikka Live Demo</div>
              <div className="text-xs text-slate-500">
                Country: <span className="font-semibold text-slate-700">{country}</span>
                {expiresLabel ? <> · expires {expiresLabel}</> : null}
              </div>
            </div>
          </div>
          {online ? (
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold"
              data-testid="live-indicator"
              title="Auto-refresh every 10 seconds"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              LIVE · auto-refresh 10s
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-300 bg-slate-100 text-slate-600 text-xs font-semibold"
              data-testid="live-indicator-offline"
            >
              <span className="h-2 w-2 rounded-full bg-slate-400" />
              OFFLINE{lastOkAt ? ` · ${fmtClock(lastOkAt)}` : ''}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Opens · last 5 min" value={data?.kpis?.last_5m ?? 0} tone="emerald" />
          <div
            className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-5 py-4 relative"
            title="Counts distinct sessions, not refreshes. No personal data stored — only a SHA-256 hash of a tab-scoped UUID."
            data-testid="live-unique-5m-card"
          >
            <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">Unique devices · 5m</div>
            <div className="text-3xl font-black mt-1 tabular-nums" data-testid="live-unique-5m">{data?.kpis?.unique_5m ?? 0}</div>
            <div className="text-[10px] text-emerald-700/70 mt-1">noise-floor · privacy-safe</div>
          </div>
          <Kpi label="Opens · last 24h" value={data?.kpis?.last_24h ?? 0} tone="sky" />
          <Kpi label="Top country" value={data?.top_country || country} tone="amber" />
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">Heartbeat</div>
              <div className="text-sm text-slate-600">Demo opens · last 5 minutes · 30-second buckets</div>
            </div>
            <div className="text-xs text-slate-500" data-testid="live-last-update">
              Last update: <span className="font-mono text-slate-700">{fmtClock(lastOkAt)}</span>
            </div>
          </div>
          <Sparkline data={series} width={960} height={80} />
        </section>

        <NordicHeatmap
          counts={data?.heatmap?.counts_5m || {}}
          totalLast5m={data?.heatmap?.total_5m || 0}
          online={online}
          trends={data?.trends?.countries || null}
          readyMode={!!data?.trends?.is_ready_mode}
          l={l}
        />

        <section className="bg-white rounded-2xl border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">Recent scans</div>
            <div className="text-xs text-slate-500">{recent.length} shown · anonymized</div>
          </div>
          {recent.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">Waiting for the first scan…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase">
                <tr>
                  <th className="px-5 py-2">Time</th>
                  <th className="px-5 py-2">Country</th>
                  <th className="px-5 py-2">Device</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r, i) => (
                  <tr key={`${r.ts}-${i}`} className="border-t border-slate-100" data-testid={`live-row-${i}`}>
                    <td className="px-5 py-2 text-xs text-slate-500 whitespace-nowrap font-mono">{fmtClock(r.ts)}</td>
                    <td className="px-5 py-2 font-semibold">
                      <span className="inline-block px-2 py-0.5 rounded border border-orange-200 bg-orange-50 text-orange-700 text-xs">{r.country || '—'}</span>
                    </td>
                    <td className="px-5 py-2 text-xs">
                      {r.device === 'mobile' ? '📱 mobile' : r.device === 'desktop' ? '🖥 desktop' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <footer className="text-center text-xs text-slate-400 pt-2 pb-8">
          Read-only live view · no personal data shown
        </footer>
      </main>
    </div>
  );
};

export default LiveDemoPage;
